import { ContractLine } from "../customer-info-store";
import { UsageIntervalEnum, UsageReport, UsageReportItem, UsageReportItemType, UsageReportPeriod } from "../usage-store";

export class ReportBuilder {
    constructor(private contractLine: ContractLine,) { }

    buildReport(usageReport: UsageReport): UsageReport {
        console.log('Build current bundle items');

        usageReport.reportItems.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

        const currentPeriodReportItems: UsageReportItem[] = this.buildCurrentReportItems(usageReport);

        console.log('build previous bundle items');

        const previousPeriodReportItems: UsageReportItem[] = this.buildPreviousReportItems(usageReport, currentPeriodReportItems);

        console.log('build planned bundle items');

        const plannedUsageReportItems: UsageReportItem[] = this.buildPlannedReportItems(previousPeriodReportItems);

        console.log('build forecast bundle items');

        const forecastUsageReportItems: UsageReportItem[] = this.buildForecastReportItems(currentPeriodReportItems, plannedUsageReportItems);

        const completeUsageReport: UsageReport = {
            usageReportPeriod: {
                startDate: this.contractLine.usageBundle.startDate,
                endDate: this.contractLine.usageBundle.endDate,
                interval: UsageIntervalEnum.Month,
            },
            reportItems: [...plannedUsageReportItems, ...currentPeriodReportItems, ...previousPeriodReportItems, ...forecastUsageReportItems]
        };

        return completeUsageReport;
    }

    getBundlePeriod(contractLine: ContractLine) {
        const currentBundlePeriod = this.getCurrentBundlePeriod(contractLine);
        const previousBundlePeriod = this.getPreviousBundlePeriod(contractLine);

        const bundlePeriod: UsageReportPeriod = {
            startDate: previousBundlePeriod.startDate,
            endDate: currentBundlePeriod.endDate,
            interval: UsageIntervalEnum.Month,
        }

        return bundlePeriod;
    }

    private getCurrentBundlePeriod(contractLine: ContractLine) {
        const endDate = new Date(contractLine.usageBundle.startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setDate(endDate.getDate() - 1);

        const currentBundlePeriod: UsageReportPeriod = {
            startDate: contractLine.usageBundle.startDate,
            endDate: this.formatDate(endDate),
            interval: UsageIntervalEnum.Month,
        };

        return currentBundlePeriod;
    }

    private getPreviousBundlePeriod(contractLine: ContractLine): UsageReportPeriod {
        const startDate = new Date(contractLine.usageBundle.startDate);

        const previousStartDate = new Date(startDate);
        previousStartDate.setFullYear(previousStartDate.getFullYear() - 1);

        const previousEndDate = new Date(startDate);
        previousEndDate.setDate(previousEndDate.getDate() - 1);

        const previousBundlePeriod: UsageReportPeriod = {
            startDate: this.formatDate(previousStartDate),
            endDate: this.formatDate(previousEndDate),
            interval: UsageIntervalEnum.Month,
        };

        return previousBundlePeriod;
    }

    private formatDate(date: Date) {
        const year = date.getFullYear();
        const month = this.padStart(String(date.getMonth() + 1));
        const day = this.padStart(String(date.getDate()));
        return `${year}-${month}-${day}`;
    };

    private padStart(value: string) {
        if (value.length == 1) {
            return `0${value}`;
        }
        return value;
    }

    private buildCurrentReportItems(usageReport: UsageReport): UsageReportItem[] {
        // split in half to get the current period
        let currentPeriodUsageReportItems = usageReport.reportItems.slice(usageReport.reportItems.length / 2, usageReport.reportItems.length);

        const currentMonthItemIndex = this.getCurrentMonthsItemIndex(currentPeriodUsageReportItems);

        currentPeriodUsageReportItems = currentPeriodUsageReportItems.slice(0, currentMonthItemIndex + 1);

        let cumulativeCount = this.contractLine.usageBundle.transferred || 0; // Start with the usage transferred from current bundle

        // add first point
        const result: UsageReportItem[] = [{
            type: UsageReportItemType.Actual,
            startDate: currentPeriodUsageReportItems[0].startDate,
            endDate: null,
            xAxisValue: new Date(currentPeriodUsageReportItems[0].startDate),
            count: cumulativeCount,
            cumulativeCount: cumulativeCount,
            isStartItem: true
        }];

        for (let i = 0; i < currentPeriodUsageReportItems.length; i++) {
            const currentPeriodReportItem = currentPeriodUsageReportItems[i];

            cumulativeCount += currentPeriodReportItem.count;

            result.push({
                type: UsageReportItemType.Actual,
                startDate: currentPeriodReportItem.startDate,
                endDate: currentPeriodReportItem.endDate,
                xAxisValue: new Date(currentPeriodReportItem.endDate),
                count: currentPeriodReportItem.count,
                cumulativeCount: cumulativeCount,
            });
        }

        // set last point to today, as this can be anywhere in the current month
        result[result.length - 1].xAxisValue = new Date();
        result[result.length - 1].isCurrentItem = true;

        return result;
    }

    private buildPreviousReportItems(usageReport: UsageReport, currentPeriodReportItems: UsageReportItem[]): UsageReportItem[] {
        // split in half to get the previous period
        const previousPeriodUsageReportItems = usageReport.reportItems.slice(0, usageReport.reportItems.length / 2);

        let cumulativeCount = this.contractLine.usageBundle.transferredPreviousBundle || 0; // Start with the usage transferred from previous bundle

        // add first point
        const result: UsageReportItem[] = [{
            type: UsageReportItemType.ActualPrevious,
            startDate: currentPeriodReportItems[0].startDate,
            endDate: currentPeriodReportItems[0].endDate,
            xAxisValue: new Date(currentPeriodReportItems[0].startDate),
            count: cumulativeCount,
            cumulativeCount: cumulativeCount,
            isStartItem: true
        }];


        let startDate = new Date(currentPeriodReportItems[0].startDate);
        let endDate = this.getNextEndDate(startDate);

        for (let i = 0; i < previousPeriodUsageReportItems.length; i++) {
            const previousPeriodReportItem = previousPeriodUsageReportItems[i];

            cumulativeCount += previousPeriodReportItem.count;

            result.push({
                type: UsageReportItemType.ActualPrevious,
                startDate: this.formatDate(startDate),
                endDate: this.formatDate(endDate),
                xAxisValue: new Date(endDate),
                count: previousPeriodReportItem.count,
                cumulativeCount: cumulativeCount,
            });

            startDate = this.getNextStartDate(startDate);
            endDate = this.getNextEndDate(startDate)
        }

        return result;
    }

    getNextStartDate(currentEndDate: Date): Date {
        const nextStartDate = new Date(currentEndDate);
        nextStartDate.setMonth(nextStartDate.getMonth() + 1)
        return nextStartDate;
    }

    getNextEndDate(startDate: Date): Date {
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + 1); // Add one month
        endDate.setSeconds(endDate.getSeconds() - 1); // Subtract one second
        return endDate;
    }

    private buildPlannedReportItems(previousPeriodReportItems: UsageReportItem[]): UsageReportItem[] {
        // This function takes the 'previous period items' as input, as this is for sure a full year ('current period items' usually is part of a year, up to today)

        let monthlyQuantity = (this.contractLine.quantity - this.contractLine.usageBundle.transferred) / 12;

        let cumulativeCount = this.contractLine.usageBundle.transferred || 0; // Start with the usage transferred from current bundle

        const result: UsageReportItem[] = [{
            type: UsageReportItemType.Planned,
            startDate: previousPeriodReportItems[0].startDate,
            endDate: previousPeriodReportItems[0].endDate,
            xAxisValue: new Date(previousPeriodReportItems[0].startDate),
            count: cumulativeCount,
            cumulativeCount: cumulativeCount,
            isStartItem: true
        }];

        for (let i = 1; i < previousPeriodReportItems.length; i++) {
            const previousPeriodReportItem = previousPeriodReportItems[i];

            cumulativeCount += monthlyQuantity;

            result.push({
                type: UsageReportItemType.Planned,
                startDate: previousPeriodReportItem.startDate,
                endDate: previousPeriodReportItem.endDate,
                xAxisValue: previousPeriodReportItem.xAxisValue,
                count: monthlyQuantity,
                cumulativeCount: cumulativeCount,
            });
        }

        return result;
    }

    private getCurrentMonthsItemIndex(currentPeriodReportItems: UsageReportItem[]): number {
        const today = new Date();

        return currentPeriodReportItems.findIndex(item => {
            const startDate = new Date(item.startDate);
            const endDate = new Date(item.endDate);
            // Only evaluate real periods (no starting points)
            if (startDate <= today && endDate >= today && item.isStartItem !== true) {
                return true;
            }

            return false;
        });
    }

    private getCurrentMonthsForecastedCount(currentPeriodReportItems: UsageReportItem[], currentMonthsItemIndex: number): number {
        const today = new Date();
        const currentMonthItem = currentPeriodReportItems[currentMonthsItemIndex];
        const currentMonthItemEndDate = new Date(currentMonthItem.endDate);
        return currentMonthItem.count / today.getDate() * currentMonthItemEndDate.getDate();
    }

    private buildForecastReportItems(currentPeriodReportItems: UsageReportItem[], plannedReportItems: UsageReportItem[]): UsageReportItem[] {
        // Get current month item
        const currentMonthsItemIndex = this.getCurrentMonthsItemIndex(currentPeriodReportItems);
        const currentMonthItem = currentPeriodReportItems[currentMonthsItemIndex];

        // Now calculate the forecast for current month and remaining months
        // NOTE: use the usagebundle's forecast as a base, to ensure that both tile and report show the same forecast

        // Complete the current month based on the forecasted usage per day (cumulative count per today + forecastPerDay for remaining days)
        const forecastedUsagePerDay = this.contractLine.usageBundle.forecast / 365; // Forget about leap year
        const remainingDaysInMonth = this.getDiffInDays(new Date(), new Date(currentMonthItem.endDate));
        const cumulativeCountIncludingCurrentMonthsForecast = currentMonthItem.cumulativeCount + (remainingDaysInMonth * forecastedUsagePerDay);

        // Calculate the forecasted usage per month for the remaining months (ensure that the final count is equal to the bundle's forecast)
        const remainingMonthsInPeriod = plannedReportItems.length - currentMonthsItemIndex - 1; // Minus 1 as current month's index is zero-based
        const remainingForecastedUsage = this.contractLine.usageBundle.forecast - cumulativeCountIncludingCurrentMonthsForecast;
        const forecastedRemainingUsagePerMonth = remainingForecastedUsage / remainingMonthsInPeriod;

        // add first point where actual stops
        const result: UsageReportItem[] = [{
            type: UsageReportItemType.Forecast,
            startDate: currentMonthItem.startDate,
            endDate: currentMonthItem.endDate,
            xAxisValue: currentMonthItem.xAxisValue,
            count: currentMonthItem.count,
            cumulativeCount: currentMonthItem.cumulativeCount,
        }];

        let forecastedCount = cumulativeCountIncludingCurrentMonthsForecast;
        for (let i = currentMonthsItemIndex; i < plannedReportItems.length; i++) {
            result.push({
                type: UsageReportItemType.Forecast,
                startDate: plannedReportItems[i].startDate,
                endDate: plannedReportItems[i].endDate,
                xAxisValue: plannedReportItems[i].xAxisValue,
                count: forecastedRemainingUsagePerMonth,
                cumulativeCount: forecastedCount,
            });
            forecastedCount += forecastedRemainingUsagePerMonth;
        }

        return result;
    }

    private getDiffInDays(startDate: Date, endDate: Date): number {
        const diffTime = endDate.getTime() - startDate.getTime(); // Calculate diff in milliseconds
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
    }
}


// WEBPACK FOOTER //
// ./ClientApp/app/services/usage/report-builder.ts